import { Container, IDestroyOptions, Point, Sprite, Text, TextStyle } from "pixi.js";
import { GameFont } from "../../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { LoginResourcePackage } from "../../resources/LoginResourcePackage";
import { Event } from "event-system";
import { IGuestModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IGuestModel";
import { DropShadowFilter } from "pixi-filters";
import { Avatar } from "by/gamefactory/cityversetycoon/scenes/common/components/Avatar";
import { UserProfileResourcePackage } from "../../../userprofile/resources/UserProfileResourcePackage";

export class SignInSuccessfulContainer extends Container {
    public readonly onOk: Event;
    private static readonly HEADER_TEXT = "SIGN IN\nSUCCESSFUL";
    private static readonly DESCRIPTION_TEXT = "SIGNED IN\nSUCCESSFULLY.";
    private static readonly CONFIRM_BUTTON_TEXT = "OK";

    private readonly loginResources: LoginResourcePackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly guestModel: IGuestModel;
    private confirmButton: ButtonWithScaleAnimation;
    private buttonShadowFilter: DropShadowFilter;

    public constructor(loginResources: LoginResourcePackage, userProfileResources: UserProfileResourcePackage, guestModel: IGuestModel) {
        super();
        this.loginResources = loginResources;
        this.userProfileResources = userProfileResources;
        this.guestModel = guestModel;
        this.onOk = new Event();
        this.build();
    }

    private build(): void {
        const frame = new Sprite(this.loginResources.assets_views_login_verificationFailed_glas_tooltip_frame_png);
        frame.anchor.x = 0.5;
        frame.width = 555;
        frame.height = 774;
        this.addChild(frame);

        const headerText = new Text(SignInSuccessfulContainer.HEADER_TEXT, new HeaderTextStyle());
        headerText.anchor.set(0.5, 0);
        headerText.skew.x = -0.087;
        headerText.angle = -5;
        headerText.position.y = frame.y + 47;
        this.addChild(headerText);

        const shape = new Sprite(this.loginResources.assets_views_login_verificationFailed_tooltip_purple_shape_png);
        shape.anchor.set(0.5, 0);
        shape.width = 555;
        shape.height = 478;
        shape.position.y = headerText.y + headerText.height + 27;
        this.addChild(shape);

        const avatar = new Avatar(this.userProfileResources, this.guestModel.avatarTexture.value);
        avatar.avatarSize = new Point(145, 145);
        avatar.glow = true;
        avatar.enabled = false;
        avatar.position.set(-72, shape.position.y - 13);
        this.addChild(avatar);

        const descriptionText = new Text(SignInSuccessfulContainer.DESCRIPTION_TEXT, new DescriptionTextStyle());
        descriptionText.anchor.set(0.5, 0);
        descriptionText.position.y = shape.y + 165;
        this.addChild(descriptionText);

        this.confirmButton = new ButtonWithScaleAnimation(this.loginResources.assets_views_login_buttons_button_green_png);
        this.confirmButton.position.set(-this.confirmButton.width * 0.5, descriptionText.position.y + descriptionText.getLocalBounds().height + 26);
        this.confirmButton.onUp.add(this.confirmButtonClickHandle, this);
        const confirmButtonText = new Text(SignInSuccessfulContainer.CONFIRM_BUTTON_TEXT, new ButtonTextStyle());
        confirmButtonText.position.set(this.confirmButton.width * 0.5 - confirmButtonText.width * 0.5, this.confirmButton.height * 0.5 - confirmButtonText.height * 0.5);
        this.confirmButton.addChild(confirmButtonText);
        this.addChild(this.confirmButton);

        this.buttonShadowFilter = new DropShadowFilter();
        this.buttonShadowFilter.offset = new Point(3, 3);
        this.buttonShadowFilter.blur = 2;
        this.buttonShadowFilter.color = 0x000000;
        this.confirmButton.filters = [this.buttonShadowFilter];
    }

    private async confirmButtonClickHandle(): Promise<void> {
        this.confirmButton.eventMode = 'none';
        this.onOk.emit();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.confirmButton.onUp.remove(this.confirmButtonClickHandle, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 128,
            lineHeight: 102.4
        }));
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 7,
            dropShadowBlur: 5,
            dropShadowDistance: 3,
            dropShadowAlpha: 0.5,
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            lineHeight: 64,
            letterSpacing: 0.5
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.8,
            dropShadowBlur: 5,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 57,
            miterLimit: 0
        }));
    }
}
